/**
 * DoorOpenerCommand.js
 * Kapı Otomatiği (K.O.) + bağlantı wire'ı ekleme/silme komutları.
 * BellButtonCommand deseninin K.O. sembolü/tipiyle uyarlaması.
 *
 * K.O. eklenirken:
 * 1. K.O. sembolü sahneye eklenir
 * 2. İnterkom hattından K.O.'ya bağlantı wire'ı (yeşil polyline) eklenir
 */

import BaseCommand from './BaseCommand';
import { addWire, deleteWire } from '../../../redux/wireSlice';
import * as THREE from 'three';
import { createDoorOpener } from '../../../factory/symbols/createDoorOpener';
import sceneObjectCache from '../SceneObjectCache';
import { batch } from 'react-redux';

class DoorOpenerCommand extends BaseCommand {
  constructor(action, sceneData) {
    super(action === 'ADD' ? 'ADD_DOOR_OPENER' : 'DELETE_DOOR_OPENER', {
      before: action === 'DELETE' ? { sceneData } : null,
      after: action === 'ADD' ? { sceneData } : null
    }, sceneData);

    this.action = action;
  }

  execute(scene, dispatch, skipRedux = false) {
    if (this.action === 'ADD') {
      this._addDoorOpener(scene, dispatch, skipRedux);
    } else if (this.action === 'DELETE') {
      this._deleteDoorOpener(scene, dispatch, skipRedux);
    }
  }

  undo(scene, dispatch) {
    if (this.action === 'ADD') {
      this._deleteDoorOpener(scene, dispatch);
    } else if (this.action === 'DELETE') {
      this._addDoorOpener(scene, dispatch);
    }
  }

  _addDoorOpener(scene, dispatch, skipRedux = false) {
    const wireColor = this.sceneData.color || 0x33cc33;

    // 1. K.O. sembolü
    const koPosition = new THREE.Vector3(
      this.sceneData.buttonPosition[0],
      this.sceneData.buttonPosition[1],
      this.sceneData.buttonPosition[2]
    );

    const doorOpener = createDoorOpener(scene, koPosition, wireColor, false);
    doorOpener.rotation.z = this.sceneData.buttonAngle || 0;
    doorOpener.userData = {
      ...doorOpener.userData,
      id: this.sceneData.buttonId,
      branchID: this.sceneData.branchID,
      wireId: this.sceneData.wireId,
      type: 'DoorOpener',
      isElectrical: true
    };
    scene.add(doorOpener);
    sceneObjectCache.addFixture(this.sceneData.buttonId, doorOpener);

    // 1.5. İnterkom hattı ile K.O. wire kesişimine yuvarlak buat
    if (this.sceneData.vertices && this.sceneData.vertices.length > 0) {
      const connectionPoint = new THREE.Vector3(
        this.sceneData.vertices[0].x,
        this.sceneData.vertices[0].y,
        this.sceneData.vertices[0].z || 0
      );
      const circleGeometry = new THREE.CircleGeometry(3, 16);
      const circleMaterial = new THREE.MeshBasicMaterial({ color: wireColor, side: THREE.DoubleSide });
      const connectionMarker = new THREE.Mesh(circleGeometry, circleMaterial);
      connectionMarker.position.copy(connectionPoint);
      connectionMarker.userData = {
        id: `${this.sceneData.buttonId}_connection`,
        type: 'doorOpenerConnection',
        parentId: this.sceneData.buttonId,
        branchID: this.sceneData.branchID
      };
      scene.add(connectionMarker);
      sceneObjectCache.addFixture(`${this.sceneData.buttonId}_connection`, connectionMarker);
    }

    // 2. Bağlantı wire'ı (yeşil polyline)
    if (this.sceneData.vertices && this.sceneData.vertices.length > 1) {
      const wireVertices = this.sceneData.vertices.map(v => new THREE.Vector3(v.x, v.y, v.z));
      const wireGeometry = new THREE.BufferGeometry().setFromPoints(wireVertices);
      const wireMaterial = new THREE.LineBasicMaterial({ color: wireColor });
      const wireLine = new THREE.Line(wireGeometry, wireMaterial);
      wireLine.userData = {
        id: this.sceneData.wireId,
        type: 'doorOpenerWire',
        wireId: this.sceneData.wireId,
        branchID: this.sceneData.branchID,
        isSelectable: true
      };
      scene.add(wireLine);
      sceneObjectCache.addFeeder(this.sceneData.wireId, wireLine);

      if (!skipRedux) {
        dispatch(addWire({
          id: this.sceneData.wireId,
          sceneUUID: wireLine.uuid,
          type: 'polyline',
          vertices: this.sceneData.vertices,
          layer: 'doorOpener',
          color: wireColor,
          userData: {
            selectable: true,
            parent: 'doorOpener',
            branchID: this.sceneData.branchID
          }
        }));
      }
    }
  }

  _deleteDoorOpener(scene, dispatch) {
    batch(() => {
      dispatch(deleteWire(this.sceneData.wireId));
    });

    const koToRemove = sceneObjectCache.getFixture(this.sceneData.buttonId);
    if (koToRemove) {
      koToRemove.traverse(child => {
        if (child.geometry) child.geometry.dispose();
        if (child.material) {
          if (Array.isArray(child.material)) child.material.forEach(mat => mat.dispose());
          else child.material.dispose();
        }
      });
      if (koToRemove.parent) koToRemove.parent.remove(koToRemove);
      else scene.remove(koToRemove);
      sceneObjectCache.removeFixture(this.sceneData.buttonId);
    }

    const connectionMarkerToRemove = sceneObjectCache.getFixture(`${this.sceneData.buttonId}_connection`);
    if (connectionMarkerToRemove) {
      if (connectionMarkerToRemove.geometry) connectionMarkerToRemove.geometry.dispose();
      if (connectionMarkerToRemove.material) connectionMarkerToRemove.material.dispose();
      scene.remove(connectionMarkerToRemove);
      sceneObjectCache.removeFixture(`${this.sceneData.buttonId}_connection`);
    }

    const wireToRemove = sceneObjectCache.getFeeder(this.sceneData.wireId);
    if (wireToRemove) {
      if (wireToRemove.geometry) wireToRemove.geometry.dispose();
      if (wireToRemove.material) wireToRemove.material.dispose();
      scene.remove(wireToRemove);
      sceneObjectCache.removeFeeder(this.sceneData.wireId);
    }
  }

  static fromJSON(json) {
    const action = json.type === 'ADD_DOOR_OPENER' ? 'ADD' : 'DELETE';
    return new DoorOpenerCommand(action, json.sceneData);
  }
}

export default DoorOpenerCommand;
